// TeamRecap.js — a team's event as a story, the way Spotify Wrapped tells a year.
//
// Full screen, one scene at a time: progress bars on top, tap right/left to move, hold to pause,
// numbers that count, confetti for a podium — and an upbeat CC0 track underneath
// (public/sounds/recap-upbeat.mp3, see CREDITS.md). A browser only plays sound after a tap, so
// the story opens on a "tap to start" cover and the first tap starts the music with it.
//
// Public on purpose (/public-team/:teamId/recap): a recap is made to be shared, and the team
// page it hangs off already shows all of this. The data is the same dict Machina draws its
// slides from (shared_models.services.team_recap) — a scene with nothing to show is left out.
// Downloads (PDF / slides) appear only for the team itself and its contact person; the photos
// ZIP only where the API offers it (can_download_photos — off in Tornea, see team_recap.py).
//
// ★ PREVIEW: the team itself, before its event, sees every empty spot as a placeholder that says
// what will go there ("Your team photo goes here") with a button to fill it in — the recap is the
// teaser that gets profiles completed (Home shows it as a tile on PLG). Which spots are empty
// comes from the team's readiness checks, so an event without jokers never asks for one. Anyone
// else only ever sees the scenes there is real content for.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Icon from '../../components/common/Icon';
import { apiRequest, awardArt, teamPhoto } from '../../utils';
import RecapImagesButton from './RecapImagesButton';
import './TeamRecap.css';

const MUSIC = '/sounds/recap-upbeat.mp3';
const MEDAL = { 1: '#f5c542', 2: '#c9d1d9', 3: '#d9894a' };

// ------------------------------------------------------------------ colour
const hexToHsl = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return [200, 80, 30];
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
};

// Bold, Wrapped-like pairs around the brand colour: each scene its own mood, all one family.
const palette = (primary, shift) => {
  const [h, s] = hexToHsl(primary);
  const hue = (h + shift + 360) % 360;
  const sat = Math.max(65, Math.min(90, s));
  return {
    '--tr-a': `hsl(${hue} ${sat}% 52%)`,
    '--tr-b': `hsl(${(hue + 40) % 360} ${sat}% 22%)`,
    '--tr-blob1': `hsl(${(hue + 320) % 360} ${sat}% 60%)`,
    '--tr-blob2': `hsl(${(hue + 60) % 360} ${sat}% 55%)`,
  };
};

// ------------------------------------------------------------------ helpers
const useCountUp = (target, { from = 0, duration = 1600, delay = 0, paused = false } = {}) => {
  const [value, setValue] = useState(from);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    let raf;
    let elapsed = -delay;
    let last = performance.now();
    const tick = (now) => {
      if (!pausedRef.current) elapsed += now - last;
      last = now;
      const p = Math.max(0, Math.min(1, elapsed / duration));
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, from, duration, delay]);
  return value;
};

const Confetti = ({ count = 44 }) => {
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2.4 + Math.random() * 1.8,
    rotate: Math.random() * 360,
    color: ['#f5c542', '#ff5a8a', '#4cc9f0', '#7cff6b', '#ffffff', '#ff8a3d'][i % 6],
  })), [count]);
  return (
    <div className="tr-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span key={i} style={{
          left: `${p.left}%`, background: p.color, animationDelay: `${p.delay}s`,
          animationDuration: `${p.duration}s`, transform: `rotate(${p.rotate}deg)`,
        }} />
      ))}
    </div>
  );
};

const Avatar = ({ src, name, className = '' }) => {
  const [broken, setBroken] = useState(false);
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return src && !broken
    ? <img className={className} src={src} alt="" onError={() => setBroken(true)} />
    : <span className={`${className} tr-initials`}>{initials}</span>;
};

const d = (ms) => ({ '--d': `${ms}ms` });
const isNumeric = (v) => typeof v === 'number' || /^\d+$/.test(String(v));

// Where each readiness check is fixed (the same map as TeamReadiness.js).
const FIX_ROUTE = {
  captain: '/players', players: '/players', all_player_photos: '/players',
  photo: '/edit-team', slogan: '/edit-team', bio: '/edit-team', song: '/edit-team',
  joker: '/battles', challenged: '/teams', mission: '/missions',
};
const fixRoute = (key, teamId) => (key === 'role' ? `/public-team/${teamId}` : FIX_ROUTE[key] || '/home');
// An empty spot the preview shows: the check applies to this event and is not done yet.
const needs = (data, key) => !!data.preview && (data.readiness?.checks || []).some((c) => c.key === key && !c.done);

// "Fill it in": a button inside the story, so it must not also count as a tap on the story.
const FillLink = ({ to, teamId, children }) => {
  const navigate = useNavigate();
  const stop = (e) => e.stopPropagation();
  return (
    <button type="button" className="tr-fill" onPointerDown={stop} onPointerUp={stop}
            onClick={(e) => {
              e.stopPropagation();
              try { sessionStorage.setItem('otherTeamId', teamId); } catch { /* storage blocked */ }
              navigate(to);
            }}>
      <Icon name="plus" /> {children}
    </button>
  );
};

// ------------------------------------------------------------------ scenes
const Intro = ({ data, t, dateLabel, teamId }) => {
  const { team, event } = data;
  const ghostPhoto = needs(data, 'photo');
  return (
    <div className="tr-scene tr-intro">
      <div className="tr-kicker tr-up" style={d(100)}>
        {event.is_over ? t('recapIntro', { event: event.name }) : t('recapIntroBefore', { event: event.name })}
      </div>
      {ghostPhoto ? (
        <div className="tr-hero is-ghost tr-zoom" style={d(250)}>
          <div className="tr-ghost-inner">
            <Icon name="camera" />
            <span>{t('recapGhostPhoto')}</span>
            <FillLink to={fixRoute('photo')} teamId={teamId}>{t('recapAddNow')}</FillLink>
          </div>
        </div>
      ) : (
        <div className="tr-hero tr-zoom" style={d(250)}>
          <img src={teamPhoto(team.photo_url)} alt="" />
        </div>
      )}
      <h1 className="tr-title tr-up" style={d(650)}>{team.name}</h1>
      {team.slogan && <p className="tr-slogan tr-up" style={d(1000)}>“{team.slogan}”</p>}
      {!team.slogan && needs(data, 'slogan') && (
        <p className="tr-slogan is-ghost tr-up" style={d(1000)}>“{t('recapGhostSlogan')}”</p>
      )}
      {dateLabel && <div className="tr-meta tr-up" style={d(1300)}>{dateLabel}</div>}
    </div>
  );
};

const Lineup = ({ data, t, teamId }) => {
  const players = data.players.slice(0, 12);
  // In preview, a team under four players gets the empty seats drawn in.
  const openSeats = needs(data, 'players') ? Math.max(0, 4 - players.length) : 0;
  const big = players.length + openSeats <= 4;
  const ghostPhotos = needs(data, 'all_player_photos');
  const ghostRoles = needs(data, 'role');
  const fixKey = ['players', 'all_player_photos', 'role'].find((k) => needs(data, k));
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapLineup')}</div>
      <h2 className="tr-heading tr-up" style={d(200)}>{t('recapMeetTeam')}</h2>
      <div className={`tr-lineup ${big ? 'is-big' : ''}`}>
        {players.map((p, i) => {
          const role = p.role;
          const roleName = role ? (role.key ? t(`roles:${role.key}.name`) : role.name) : null;
          return (
            <div key={i} className="tr-player tr-pop" style={d(450 + i * 140)}>
              <div className={`tr-avatar-ring ${p.is_captain ? 'is-captain' : ''} ${ghostPhotos && !p.photo_url ? 'is-ghost' : ''}`}>
                <Avatar src={p.photo_url} name={p.full_name} className="tr-avatar" />
                {p.is_captain && <span className="tr-crown"><Icon name="crown" /></span>}
              </div>
              <div className="tr-player-name">{p.first_name || p.full_name}</div>
              {roleName && (
                <span className="tr-role" style={{ background: role.color || 'rgba(255,255,255,.2)' }}>{roleName}</span>
              )}
              {!roleName && ghostRoles && <span className="tr-role is-ghost">{t('recapGhostRole')}</span>}
              {!roleName && !ghostRoles && p.is_captain && <span className="tr-role is-captain">{t('recapCaptain')}</span>}
            </div>
          );
        })}
        {Array.from({ length: openSeats }, (_, i) => (
          <div key={`seat-${i}`} className="tr-player tr-pop" style={d(450 + (players.length + i) * 140)}>
            <div className="tr-avatar-ring is-ghost"><span className="tr-avatar tr-initials"><Icon name="plus" /></span></div>
            <div className="tr-player-name is-ghost">{t('recapGhostPlayer')}</div>
          </div>
        ))}
      </div>
      {fixKey && (
        <div className="tr-fill-row tr-up" style={d(1400)}>
          <FillLink to={fixRoute(fixKey, teamId)} teamId={teamId}>{t(`recapFix_${fixKey}`)}</FillLink>
        </div>
      )}
    </div>
  );
};

const Story = ({ data, t, teamId }) => {
  const { team, joker, players } = data;
  const ghostBio = !team.bio && needs(data, 'bio');
  const ghostJoker = !joker && needs(data, 'joker');
  const bio = team.bio || '';
  // A short bio gets the whole screen as a big quote; a long one steps down so it still fits.
  const size = bio.length < 140 ? 'xl' : bio.length < 320 ? 'l' : 'm';
  const captain = players.find((p) => p.is_captain);
  const facts = [
    captain && t('recapCaptainIs', { name: captain.first_name || captain.full_name }),
    players.length > 0 && t('recapPlayersCount', { count: players.length }),
    team.company,
  ].filter(Boolean);
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapStory')}</div>
      <div className="tr-story-body">
        {bio && <span className="tr-quote-mark tr-pop" style={d(150)} aria-hidden="true">“</span>}
        {bio && <p className={`tr-bio is-${size} tr-up`} style={d(350)}>{bio}</p>}
        {!bio && !ghostBio && team.slogan && <p className="tr-bio is-xl tr-up" style={d(350)}>“{team.slogan}”</p>}
        {ghostBio && (
          <div className="tr-ghost-block tr-up" style={d(350)}>
            <p className="tr-bio is-l is-ghost">{t('recapGhostBio')}</p>
            <FillLink to={fixRoute('bio')} teamId={teamId}>{t('recapAddNow')}</FillLink>
          </div>
        )}
        {facts.length > 0 && (
          <div className="tr-chips">
            {facts.map((f, i) => <span key={i} className="tr-chip tr-pop" style={d(1100 + i * 200)}>{f}</span>)}
          </div>
        )}
      </div>
      {ghostJoker && (
        <div className="tr-joker is-ghost tr-flip" style={d(1600)}>
          <span className="tr-joker-j">?</span>
          <div>
            <div className="tr-joker-label">{t('recapJoker')}</div>
            <div className="tr-joker-name">{t('recapGhostJoker')}</div>
            <FillLink to={fixRoute('joker')} teamId={teamId}>{t('recapAddNow')}</FillLink>
          </div>
        </div>
      )}
      {joker && (
        <div className="tr-joker tr-flip" style={d(bio ? 1800 : 600)}>
          {joker.photo_url ? <img src={joker.photo_url} alt="" /> : <span className="tr-joker-j">J</span>}
          <div>
            <div className="tr-joker-label">{t('recapJoker')}</div>
            <div className="tr-joker-name">{joker.name}</div>
            <div className="tr-joker-sub">
              {joker.place
                ? t('recapJokerResult', { place: joker.place, points: joker.points || 0 })
                : t('recapJokerSub')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Preview only: the results part of the recap, shown before there are results.
const AfterEvent = ({ data, t, dateLabel }) => (
  <div className="tr-scene tr-center">
    <div className="tr-kicker tr-up" style={d(50)}>{t('recapAfterEvent')}</div>
    <h2 className="tr-heading tr-up" style={d(250)}>{t('recapAfterEventHeading')}</h2>
    <div className="tr-rank is-ghost tr-pop" style={d(700)}><span>#?</span></div>
    <div className="tr-meta tr-up" style={d(1300)}>{t('recapAfterEventSub', { date: dateLabel || data.event.name })}</div>
    <div className="tr-tiles">
      {[t('recapTotal'), t('recapBattle'), t('recapBonus')].map((label, i) => (
        <div key={i} className="tr-tile is-ghost tr-up" style={d(1700 + i * 200)}><strong>?</strong><span>{label}</span></div>
      ))}
    </div>
  </div>
);

const Result = ({ data, t, paused }) => {
  const res = data.result;
  const total = res.total_teams || res.rank;
  const rank = useCountUp(res.rank, { from: total, duration: 2400, delay: 1100, paused });
  const points = useCountUp(res.points, { duration: 1500, delay: 3700, paused });
  const battle = useCountUp(res.battle_points, { duration: 1500, delay: 3900, paused });
  const bonus = useCountUp(res.bonus_points, { duration: 1500, delay: 4100, paused });
  const landed = rank === res.rank;
  const medal = MEDAL[res.rank];
  return (
    <div className="tr-scene tr-center">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapResult')}</div>
      <h2 className="tr-heading tr-up" style={d(250)}>{t('recapOutOf', { count: total })}</h2>
      <div className={`tr-rank ${landed ? 'is-landed' : ''}`} style={medal ? { '--medal': medal } : undefined}>
        <span>#{rank}</span>
      </div>
      {landed && medal && <Confetti />}
      {res.edition && (
        <div className="tr-meta tr-up" style={d(3400)}>
          {t('recapEditionPlace', { rank: res.edition.rank, count: res.edition.total_teams })}
        </div>
      )}
      <div className="tr-tiles">
        {[[points, t('recapTotal')], [battle, t('recapBattle')], [bonus, t('recapBonus')]].map(([v, label], i) => (
          <div key={i} className="tr-tile tr-up" style={d(3600 + i * 200)}>
            <strong>{v}</strong><span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Games = ({ data, t }) => {
  const games = data.games.slice(0, 10);
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapGames')}</div>
      <h2 className="tr-heading tr-up" style={d(200)}>{t('recapGamesHeading', { count: games.length })}</h2>
      <div className="tr-games">
        {games.map((g, i) => (
          <div key={i} className={`tr-game tr-slide-in ${g.is_joker ? 'is-joker' : ''}`} style={d(500 + i * 160)}>
            <span className="tr-place" style={MEDAL[g.place] ? { background: MEDAL[g.place], color: '#1b1b1b' } : undefined}>
              {g.is_disqualified ? 'DQ' : (g.place || '–')}
            </span>
            <span className="tr-game-name">{g.name}</span>
            {g.is_joker && <span className="tr-joker-tag">JOKER ×2</span>}
            <span className="tr-game-points">+{g.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Awards = ({ data, t }) => {
  const awards = data.awards.slice(0, 4);
  return (
    <div className="tr-scene tr-center">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapAwards')}</div>
      <h2 className="tr-heading tr-up" style={d(250)}>{t('recapAwardsHeading', { count: data.awards.length })}</h2>
      <div className={`tr-awards n${awards.length}`}>
        {awards.map((a, i) => (
          <div key={i} className="tr-award tr-drop" style={d(900 + i * 450)}>
            <div className="tr-award-art"><img src={awardArt(a.key, a.image)} alt="" /></div>
            <div className="tr-award-name">{a.name}</div>
          </div>
        ))}
      </div>
      <Confetti count={30} />
    </div>
  );
};

const PLATFORM = { instagram: 'Instagram', tiktok: 'TikTok', facebook: 'Facebook', linkedin: 'LinkedIn', youtube: 'YouTube', x: 'X', link: '' };

const Missions = ({ data, t, paused, teamId }) => {
  const m = data.missions;
  const ghost = !m.count;  // only reached in preview (buildScenes)
  const count = useCountUp(m.count, { duration: 1400, delay: 300, paused });
  const items = [
    ...m.photos.map((p) => ({ ...p, kind: 'photo' })),
    ...(m.videos || []).map((v) => ({ ...v, kind: 'video' })),
  ].slice(0, 6);
  const tilts = [-5, 4, -3, 6, -4, 3];
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapMissionsKicker')}</div>
      {ghost ? (
        <h2 className="tr-heading tr-up" style={d(150)}>{t('recapGhostMissions')}</h2>
      ) : (
        <h2 className="tr-heading tr-up" style={d(150)}>
          <span className="tr-big-inline">{count}</span> {t('recapMissions', { count: m.count })}
        </h2>
      )}
      {(m.points > 0 || m.pending_points > 0) && (
        <div className="tr-points-line tr-up" style={d(900)}>
          {m.points > 0 && <span className="tr-chip is-gold">{t('recapMissionPoints', { count: m.points })}</span>}
          {m.pending_points > 0 && <span className="tr-chip">{t('recapMissionPending', { count: m.pending_points })}</span>}
        </div>
      )}
      {ghost && (
        <>
          <div className="tr-polaroids n3">
            {[0, 1, 2].map((i) => (
              <figure key={i} className="tr-polaroid is-ghost tr-toss" style={{ ...d(500 + i * 260), '--tilt': `${tilts[i]}deg` }}>
                <div className="tr-ghost-tile"><Icon name="camera" /></div>
              </figure>
            ))}
          </div>
          <div className="tr-fill-row tr-up" style={d(1400)}>
            <FillLink to={fixRoute('mission')} teamId={teamId}>{t('recapFix_mission')}</FillLink>
          </div>
        </>
      )}
      <div className={`tr-polaroids n${Math.min(items.length, 3)}`}>
        {items.map((p, i) => (
          <figure key={i} className="tr-polaroid tr-toss" style={{ ...d(1100 + i * 260), '--tilt': `${tilts[i]}deg` }}>
            {p.kind === 'photo'
              ? <img src={p.url} alt="" />
              : (
                <div className="tr-video-card">
                  <span className="tr-video-play"><Icon name="play" pack="sr" /></span>
                  <span>{PLATFORM[p.platform] ? t('recapVideoOn', { platform: PLATFORM[p.platform] }) : t('recapVideo')}</span>
                </div>
              )}
            {p.name && <figcaption>{p.name}</figcaption>}
          </figure>
        ))}
      </div>
    </div>
  );
};

const SupportersGhost = ({ t, teamId }) => (
  <div className="tr-scene tr-supporters">
    <div className="tr-kicker tr-up" style={d(50)}>{t('recapSupporters')}</div>
    <h2 className="tr-heading tr-up" style={d(150)}>{t('recapGhostSupporters')}</h2>
    <div className="tr-faces is-few">
      {[0, 1, 2, 3, 4].map((i) => <span key={i} className="tr-face is-ghost tr-pop" style={d(500 + i * 120)} />)}
    </div>
    <div className="tr-bubbles">
      {[0, 1].map((i) => (
        <div key={i} className={`tr-bubble is-ghost tr-up ${i ? 'is-right' : ''}`} style={d(1200 + i * 500)}><p>“…”</p></div>
      ))}
      <p className="tr-meta tr-up" style={d(2000)}>{t('recapGhostSupportersSub')}</p>
      <div className="tr-fill-row tr-up" style={d(2200)}>
        <FillLink to="/cheers" teamId={teamId}>{t('recapShareForCheers')}</FillLink>
      </div>
    </div>
  </div>
);

const Supporters = ({ data, t, paused, teamId }) => {
  if (!data.supporters || !data.supporters.count) return <SupportersGhost t={t} teamId={teamId} />;
  return <SupportersReal data={data} t={t} paused={paused} />;
};

const SupportersReal = ({ data, t, paused }) => {
  const s = data.supporters;
  const count = useCountUp(s.count, { duration: 2200, delay: 400, paused });
  return (
    <div className="tr-scene tr-supporters">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapSupporters')}</div>
      <h2 className="tr-heading tr-up" style={d(150)}>
        <span className="tr-big-inline">{count}</span> {t('recapCheers', { count: s.count })}
      </h2>
      <div className="tr-points-line tr-up" style={d(1200)}>
        {s.people > 0 && s.people !== s.count && (
          <span className="tr-chip">{t('recapSupportersPeople', { count: s.people })}</span>
        )}
        {s.rank && s.rank <= 10 && <span className="tr-chip is-gold">{t('recapCheerRankTeam', { rank: s.rank })}</span>}
      </div>
      {s.faces.length > 0 && (
        <div className={`tr-faces ${s.faces.length <= 6 ? 'is-few' : ''}`}>
          {s.faces.slice(0, 12).map((url, i) => (
            <img key={i} className="tr-face tr-pop" style={d(1500 + i * 90)} src={url} alt="" />
          ))}
        </div>
      )}
      <div className="tr-bubbles">
        {s.messages.slice(0, 4).map((msg, i) => (
          <div key={i} className={`tr-bubble tr-up ${i % 2 ? 'is-right' : ''}`} style={d(2600 + i * 900)}>
            <Avatar src={msg.photo_url} name={msg.name} className="tr-bubble-face" />
            <div><strong>{msg.name}</strong><p>{msg.message}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Stat = ({ stat, t, delay, paused }) => {
  const numeric = isNumeric(stat.value);
  const counted = useCountUp(numeric ? Number(stat.value) : 0, { duration: 1300, delay, paused });
  return (
    <div className="tr-stat tr-up" style={d(delay)}>
      <strong>{numeric ? counted : stat.value}</strong>
      <span>{t(`recapStat_${stat.key}`)}</span>
      {stat.extra && stat.extra[0] === 'cheer_rank' && (
        <em>{t('recapCheerRank', { rank: stat.extra[1] })}</em>
      )}
    </div>
  );
};

const Stats = ({ data, t, paused }) => (
  <div className="tr-scene">
    <div className="tr-kicker tr-up" style={d(50)}>{t('recapNumbers')}</div>
    <h2 className="tr-heading tr-up" style={d(200)}>{t('recapNumbersHeading')}</h2>
    <div className="tr-stats">
      {data.stats.map((s, i) => <Stat key={s.key} stat={s} t={t} delay={500 + i * 550} paused={paused} />)}
    </div>
  </div>
);

// "I was one of the 462 ladies…" — the scene a player shares to say they were part of it: their
// own track big (ladies-only, or the mixed track powered by Hercules Trophy), the day around it.
const ONE_OF_TILES = ['teams', 'companies', 'cheers', 'battles'];

const OneOf = ({ data, t, paused }) => {
  const day = data.day;
  const kind = day.track || 'other';
  const count = useCountUp(day.track_players, { duration: 2200, delay: 800, paused });
  const event = day.ladies_event || day.track_label || data.event.name;
  const tiles = ONE_OF_TILES.filter((k) => day[k]).slice(0, 4);
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapOneOfKicker')}</div>
      <h2 className="tr-heading tr-up" style={d(250)}>
        {t(kind === 'ladies' ? 'recapOneOfIntro_ladies' : 'recapOneOfIntro_other')}
      </h2>
      <div className="tr-oneof">
        <div className="tr-oneof-num tr-pop" style={d(700)}>{count.toLocaleString()}</div>
        <div className="tr-oneof-word tr-up" style={d(1300)}>{t(`recapOneOfWord_${kind}`)}</div>
        <p className="tr-oneof-sub tr-up" style={d(2100)}>
          {t(`recapOneOfSub_${kind}`, { event, brand: day.track_label })}
        </p>
        {day.multi && day.day_players > day.track_players && (
          <div className="tr-oneof-day tr-up" style={d(2900)}>
            {t('recapOneOfDay', { count: day.day_players.toLocaleString() })}
          </div>
        )}
      </div>
      <div className="tr-tiles tr-oneof-tiles">
        {tiles.map((k, i) => (
          <div key={k} className="tr-tile tr-up" style={d(3500 + i * 200)}>
            <strong>{day[k].toLocaleString()}</strong><span>{t(`recapOneOfTile_${k}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Photos = ({ data, t, teamId }) => {
  const ghost = !data.photos.length;   // only reached in preview (buildScenes)
  return (
    <div className="tr-scene">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapPhotosKicker')}</div>
      <h2 className="tr-heading tr-up" style={d(200)}>{ghost ? t('recapGhostPhotos') : t('recapPhotos')}</h2>
      <div className={`tr-collage n${ghost ? 5 : Math.min(data.photos.length, 5)}`}>
        {ghost
          ? [0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="tr-collage-cell is-ghost tr-fade" style={d(400 + i * 200)}><Icon name="picture" /></div>
          ))
          : data.photos.slice(0, 5).map((url, i) => (
            <div key={i} className="tr-collage-cell tr-fade" style={d(400 + i * 350)}>
              <img src={url} alt="" />
            </div>
          ))}
      </div>
      {ghost && (
        <div className="tr-fill-row tr-up" style={d(1600)}>
          <FillLink to="/team-photos" teamId={teamId}>{t('recapAddPhotos')}</FillLink>
        </div>
      )}
    </div>
  );
};

// After the event, for a player of THIS team: their own invite code. A friend who opens the
// link (or the shared recap) claims a spot on the early list for the next edition.
const MyCode = ({ data, t, onShareCode, onCopyCode, shareNote }) => {
  const { my_code: mine } = data;
  return (
    <div className="tr-scene tr-center">
      <div className="tr-kicker tr-up" style={d(50)}>{t('recapCodeKicker')}</div>
      <h2 className="tr-heading tr-up" style={d(250)}>{t('recapCodeTitle')}</h2>
      <div className="tr-code tr-pop" style={d(700)}>{mine.code}</div>
      <p className="tr-slogan tr-up" style={d(1300)}>{t('recapCodeBody')}</p>
      {mine.claims > 0 && (
        <div className="tr-team-pill tr-up" style={d(1700)}>{t('referCodeClaims', { count: mine.claims })}</div>
      )}
      <div className="tr-actions tr-up" style={d(2000)} onPointerDown={(e) => e.stopPropagation()}
           onPointerUp={(e) => e.stopPropagation()}>
        <button type="button" className="tr-btn is-primary" onClick={onShareCode}>
          <Icon name="share" /> {t('recapCodeShare')}
        </button>
        <button type="button" className="tr-btn" onClick={onCopyCode}>
          <Icon name="copy" /> {t('referCodeCopy')}
        </button>
      </div>
      {shareNote && <div className="tr-toast">{shareNote}</div>}
    </div>
  );
};

const Outro = ({ data, t, dateLabel, onReplay, onShare, shareNote, teamId }) => {
  const { team, event, can_download: canDownload, theme } = data;
  const { i18n } = useTranslation();
  // The slides in the language the story was just watched in (the API ignores one it lacks).
  const lang = `?lang=${encodeURIComponent((i18n.language || '').slice(0, 2))}`;
  return (
    <div className="tr-scene tr-center tr-outro">
      {theme.logo && <img className="tr-logo tr-zoom" style={d(100)} src={theme.logo} alt="" />}
      <h1 className="tr-title tr-up" style={d(400)}>{event.is_over ? t('recapThanks') : t('recapReady')}</h1>
      <p className="tr-slogan tr-up" style={d(700)}>
        {event.is_over ? t('recapNext') : t('recapSoon', { date: dateLabel || event.name })}
      </p>
      <div className="tr-team-pill tr-up" style={d(1000)}>{team.name}</div>
      {data.preview && data.readiness && <Checklist data={data} t={t} teamId={teamId} />}
      <div className="tr-actions tr-up" style={d(1300)} onPointerDown={(e) => e.stopPropagation()}
           onPointerUp={(e) => e.stopPropagation()}>
        {data.invited_by && !data.my_code && (
          <a className="tr-btn is-primary tr-btn-wide" href={`/friend/${encodeURIComponent(data.invited_by.code)}`}>
            <Icon name="user-add" /> {data.invited_by.first_name
              ? t('recapClaimSpotFrom', { name: data.invited_by.first_name })
              : t('recapClaimSpot')}
          </a>
        )}
        <button type="button" className={`tr-btn ${data.invited_by && !data.my_code ? '' : 'is-primary'}`} onClick={onShare}>
          <Icon name="share" /> {t('recapShare')}
        </button>
        <button type="button" className="tr-btn" onClick={onReplay}>
          <Icon name="rotate-right" /> {t('recapReplay')}
        </button>
        {canDownload && (
          <>
            <a className="tr-btn" href={`/api/team-recap/${teamId}/slides.pdf${lang}`}>
              <Icon name="file-pdf" /> {t('recapDownloadPdf')}
            </a>
            <RecapImagesButton teamId={teamId} className="tr-btn" />
            {data.can_download_photos && data.photo_count + data.missions.photos.length > 0 && (
              <a className="tr-btn" href={`/api/team-recap/${teamId}/photos.zip`}>
                <Icon name="gallery" /> {t('recapDownloadPhotos')}
              </a>
            )}
          </>
        )}
        <Link className="tr-btn is-ghost" to={`/public-team/${teamId}`}>{t('recapBackToTeam')}</Link>
      </div>
      {shareNote && <div className="tr-toast">{shareNote}</div>}
    </div>
  );
};

// Preview only: what is still missing, each with its own "fill it in".
const Checklist = ({ data, t, teamId }) => {
  const checks = data.readiness.checks || [];
  const done = checks.filter((c) => c.done).length;
  return (
    <div className="tr-checklist tr-up" style={d(1200)} onPointerDown={(e) => e.stopPropagation()}
         onPointerUp={(e) => e.stopPropagation()}>
      <div className="tr-checklist-head">
        <strong>{t('recapChecklistTitle')}</strong>
        <span>{t('recapChecklistDone', { done, total: checks.length })}</span>
      </div>
      {checks.filter((c) => !c.done).slice(0, 5).map((c) => (
        <FillLink key={c.key} to={fixRoute(c.key, teamId)} teamId={teamId}>{t(`recapFix_${c.key}`)}</FillLink>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ the story
const buildScenes = (data) => {
  if (!data) return [];
  const scenes = [{ key: 'intro', C: Intro, ms: 5500, shift: 0 }];
  if (data.players.length) scenes.push({ key: 'lineup', C: Lineup, ms: 5000 + data.players.length * 350, shift: 35 });
  if (data.team.bio || data.joker || needs(data, 'bio') || needs(data, 'joker')) {
    scenes.push({ key: 'story', C: Story, ms: data.team.bio ? 10000 : 7000, shift: -35 });
  }
  if (data.preview) scenes.push({ key: 'after', C: AfterEvent, ms: 6500, shift: 180 });
  if (data.result && data.result.rank) scenes.push({ key: 'result', C: Result, ms: 8500, shift: 180 });
  if (data.games.length) scenes.push({ key: 'games', C: Games, ms: 4000 + Math.min(data.games.length, 10) * 400, shift: 70 });
  if (data.awards.length) scenes.push({ key: 'awards', C: Awards, ms: 6500, shift: 25 });
  if (data.missions.count || needs(data, 'mission')) scenes.push({ key: 'missions', C: Missions, ms: 7500, shift: -70 });
  if ((data.supporters && data.supporters.count) || data.preview) {
    const msgs = data.supporters ? Math.min(data.supporters.messages.length, 4) : 2;
    scenes.push({ key: 'supporters', C: Supporters, ms: 4500 + msgs * 1100, shift: 110 });
  }
  if (data.event.is_over && data.stats.length >= 3) scenes.push({ key: 'stats', C: Stats, ms: 3000 + data.stats.length * 700, shift: 140 });
  if (data.event.is_over && data.day && data.day.track_players) scenes.push({ key: 'oneof', C: OneOf, ms: 8500, shift: 200 });
  if (data.photos.length || data.preview) scenes.push({ key: 'photos', C: Photos, ms: 7500, shift: -110 });
  if (data.event.is_over && data.my_code) scenes.push({ key: 'code', C: MyCode, ms: 9000, shift: 60 });
  scenes.push({ key: 'outro', C: Outro, ms: 0, shift: 0 });
  return scenes;
};

export default function TeamRecap() {
  const { teamId } = useParams();
  const { t, i18n } = useTranslation(['common', 'roles']);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [run, setRun] = useState(0);              // bumps on replay so every scene re-animates
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [shareNote, setShareNote] = useState('');
  const audioRef = useRef(null);
  const holdRef = useRef({ timer: null, held: false });

  useEffect(() => {
    let alive = true;
    const ref = new URLSearchParams(window.location.search).get('ref_code') || sessionStorage.getItem('ref_code') || '';
    apiRequest('GET', `/team-recap/${teamId}${ref ? `?ref_code=${encodeURIComponent(ref)}` : ''}`)
      .then((res) => { if (alive) setData({ ...res, preview: !!res.is_team && !res.event.is_over }); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [teamId]);

  // Warm every picture before the story starts, so nothing pops in mid-scene.
  useEffect(() => {
    if (!data) return;
    const urls = [data.team.photo_url, data.theme.logo, data.joker?.photo_url,
      ...data.players.map((p) => p.photo_url), ...data.missions.photos.map((p) => p.url), ...data.photos,
      ...(data.supporters?.faces || [])];
    urls.filter(Boolean).forEach((u) => { const img = new Image(); img.src = u; });
  }, [data]);

  const scenes = useMemo(() => buildScenes(data), [data]);
  const scene = scenes[index];
  const last = index === scenes.length - 1;

  const dateLabel = useMemo(() => {
    if (!data?.event?.date) return '';
    try {
      return new Date(data.event.date).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  }, [data, i18n.language]);

  const go = useCallback((step) => {
    setIndex((i) => Math.max(0, Math.min(scenes.length - 1, i + step)));
  }, [scenes.length]);

  // Music: fade in on start, follow mute, stop when the page goes away or the tab is hidden.
  const start = () => {
    setStarted(true);
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0;
    audio.play().then(() => {
      let v = 0;
      const fade = setInterval(() => {
        v = Math.min(0.8, v + 0.08);
        audio.volume = v;
        if (v >= 0.8) clearInterval(fade);
      }, 90);
    }).catch(() => { /* no sound allowed: the story plays silent */ });
  };
  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);
  useEffect(() => {
    const audio = audioRef.current;
    const onHide = () => {
      if (document.hidden) { setPaused(true); audio?.pause(); } else if (started) { setPaused(false); audio?.play().catch(() => {}); }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => { document.removeEventListener('visibilitychange', onHide); audio?.pause(); };
  }, [started]);

  useEffect(() => {
    const onKey = (e) => {
      if (!started) return;
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === ' ') setPaused((p) => !p);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, go]);

  // Tap left third = back, elsewhere = forward; press and hold = pause (like a story).
  const onPointerDown = () => {
    holdRef.current.held = false;
    holdRef.current.timer = setTimeout(() => { holdRef.current.held = true; setPaused(true); }, 230);
  };
  const onPointerUp = (e) => {
    clearTimeout(holdRef.current.timer);
    if (holdRef.current.held) { setPaused(false); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    go(e.clientX - rect.left < rect.width * 0.3 ? -1 : 1);
  };

  const replay = () => { setIndex(0); setRun((r) => r + 1); setPaused(false); };
  const note = (msg) => { setShareNote(msg); setTimeout(() => setShareNote(''), 2500); };
  // A player's shared recap carries their code, so whoever watches it ends on "claim your spot".
  const recapUrl = () => (data.my_code
    ? `${window.location.origin}/public-team/${teamId}/recap?ref_code=${encodeURIComponent(data.my_code.code)}`
    : window.location.href);
  const shareCode = async () => {
    const { code, link } = data.my_code;
    const text = t('referCodeShareText', { code, brand: data.event.name });
    try {
      if (navigator.share) { await navigator.share({ title: code, text, url: link }); return; }
      await navigator.clipboard.writeText(`${text} ${link}`);
      note(t('recapLinkCopied'));
    } catch { /* cancelled */ }
  };
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(data.my_code.link); note(t('recapLinkCopied')); } catch { /* no clipboard */ }
  };
  const share = async () => {
    const url = recapUrl();
    const text = t('recapShareText', { team: data.team.name, event: data.event.name });
    try {
      if (navigator.share) { await navigator.share({ title: data.team.name, text, url }); return; }
      await navigator.clipboard.writeText(url);
      note(t('recapLinkCopied'));
    } catch { /* cancelled */ }
  };

  if (failed) {
    return (
      <div className="tr-root">
        <div className="tr-stage tr-center tr-message">
          <p>{t('recapNotFound')}</p>
          <Link className="tr-btn" to={`/public-team/${teamId}`}>{t('recapBackToTeam')}</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="tr-root"><div className="tr-stage tr-center"><div className="tr-spinner" /></div></div>;
  }

  const style = palette(data.theme.primary, started ? scene.shift : 0);
  const Scene = scene.C;
  return (
    <div className="tr-root">
      <audio ref={audioRef} src={MUSIC} loop preload="auto" />
      <div className={`tr-stage ${paused ? 'is-paused' : ''}`} style={style}>
        <div className="tr-blob tr-blob1" />
        <div className="tr-blob tr-blob2" />

        {!started ? (
          <button type="button" className="tr-cover" onClick={start}>
            <img className="tr-cover-bg" src={teamPhoto(data.team.photo_url)} alt="" />
            <div className="tr-cover-body">
              <div className="tr-kicker">{data.event.name}</div>
              <h1 className="tr-title">{data.team.name}</h1>
              <span className="tr-play"><Icon name="play" pack="sr" /></span>
              <div className="tr-meta">{t('recapTapToStart')}</div>
            </div>
          </button>
        ) : (
          <>
            <div className="tr-bars">
              {scenes.map((s, i) => (
                <span key={s.key} className="tr-bar">
                  {i < index && <i className="is-done" />}
                  {i === index && (s.ms
                    ? <i key={`${run}-${index}`} className="is-live"
                         style={{ animationDuration: `${s.ms}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                         onAnimationEnd={() => go(1)} />
                    : <i className="is-done" />)}
                </span>
              ))}
            </div>
            <div className="tr-top">
              <span className="tr-top-team">{data.team.name}</span>
              {data.preview && <span className="tr-preview-chip">{t('recapPreviewBadge')}</span>}
              <button type="button" className="tr-icon-btn" aria-label={muted ? t('recapUnmute') : t('recapMute')}
                      onClick={() => setMuted((m) => !m)}>
                <Icon name={muted ? 'volume-mute' : 'volume'} />
              </button>
              <Link className="tr-icon-btn" to={`/public-team/${teamId}`} aria-label={t('recapBackToTeam')}>
                <Icon name="cross" />
              </Link>
            </div>
            <div className="tr-tap" onPointerDown={last ? undefined : onPointerDown}
                 onPointerUp={last ? undefined : onPointerUp}
                 onPointerLeave={() => { clearTimeout(holdRef.current.timer); if (holdRef.current.held) setPaused(false); }}>
              <Scene key={`${run}-${scene.key}`} data={data} t={t} paused={paused} dateLabel={dateLabel}
                     onReplay={replay} onShare={share} onShareCode={shareCode} onCopyCode={copyCode}
                     shareNote={shareNote} teamId={teamId} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
